import React, { useRef, useState } from 'react';
import './CurierComfirm.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../Firebaseconfig';

const VEHICLES = [
  {
    id: 'bicycle',
    label: 'Bicycle',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="5.5" cy="17.5" r="3.5"/><circle cx="18.5" cy="17.5" r="3.5"/>
        <path d="M15 6a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm-3 11.5L9 10l-4 3h6.5"/>
        <path d="M15 6l2 4.5H9"/>
      </svg>
    ),
  },
  {
    id: 'scooter',
    label: 'Scooter',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="7" cy="17" r="2.5"/><circle cx="17" cy="17" r="2.5"/>
        <path d="M5 17H3v-3l2-5h7l2 4h2l1.5-2H20"/>
        <path d="M10 9V7h4l2 5"/>
      </svg>
    ),
  },
  {
    id: 'car',
    label: 'Car',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 17H3v-5l2-5h14l2 5v5h-2"/>
        <circle cx="7.5" cy="17.5" r="2.5"/><circle cx="16.5" cy="17.5" r="2.5"/>
        <path d="M5 12h14"/>
      </svg>
    ),
  },
  {
    id: 'van',
    label: 'Van',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="7" width="15" height="11" rx="1"/>
        <path d="M16 10l4 3v5h-4V10z"/>
        <circle cx="5.5" cy="18.5" r="2"/><circle cx="18.5" cy="18.5" r="2"/>
        <path d="M1 13h15"/>
      </svg>
    ),
  },
];

function CurierComfirm() {
  const navigate = useNavigate();
  const location = useLocation();
  const signupData = location.state?.signupData || null;

  const fileInputRef = useRef(null);

  const [firstName, setFirstName] = useState(signupData?.firstName || '');
  const [lastName, setLastName] = useState(signupData?.lastName || '');
  const [email, setEmail] = useState(signupData?.email || '');
  const [vehicle, setVehicle] = useState('bicycle');
  const [vehicleFile, setVehicleFile] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    if (e.target.files[0]) setVehicleFile(e.target.files[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files[0]) setVehicleFile(e.dataTransfer.files[0]);
  };

  const handleSubmit = async () => {
    if (!firstName || !lastName || !email) {
      setError('Please fill out all required fields.');
      return;
    }
    if (!vehicleFile) {
      setError('Please upload proof of your vehicle.');
      return;
    }
    setError('');
    setIsSubmitting(true);

    try {
      const password = signupData?.password;
      if (!password) throw new Error('Missing password. Please restart sign-up.');

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        firstName,
        lastName,
        email,
        role: 'courier',
        vehicleType: vehicle,
        status: 'Active',
        createdAt: new Date().toISOString(),
      });

      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="cc-page">
      {/* ── Hero ── */}
      <div className="cc-hero">
        <h1 className="cc-hero-title">
          Drive with <span className="cc-brand">CraveDash</span>
        </h1>
        <p className="cc-hero-sub">
          Fill out the form below to start your journey as a delivery partner and unlock
          premium earning potential.
        </p>
      </div>

      {/* ── Card ── */}
      <div className="cc-card">

        {/* First / Last Name */}
        <div className="cc-row">
          <div className="cc-field">
            <label className="cc-label">First Name</label>
            <input
              className="cc-input"
              type="text"
              placeholder="John"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div className="cc-field">
            <label className="cc-label">Last Name</label>
            <input
              className="cc-input"
              type="text"
              placeholder="Doe"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>

        {/* Email */}
        <div className="cc-field">
          <label className="cc-label">Email Address</label>
          <div className="cc-input-wrap">
            <svg className="cc-input-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            <input
              className="cc-input cc-input--icon"
              type="email"
              placeholder="john.doe@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>

        {/* Vehicle Selection */}
        <div className="cc-field">
          <label className="cc-label">Vehicle Selection</label>
          <div className="cc-vehicles">
            {VEHICLES.map((v) => (
              <button
                key={v.id}
                type="button"
                className={`cc-vehicle-btn ${vehicle === v.id ? 'cc-vehicle-btn--active' : ''}`}
                onClick={() => setVehicle(v.id)}
              >
                <span className="cc-vehicle-icon">{v.icon}</span>
                <span className="cc-vehicle-label">{v.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Vehicle Proof */}
        <div className="cc-field">
          <label className="cc-label">Vehicle Proof</label>
          <div
            className={`cc-upload ${vehicleFile ? 'cc-upload--filled' : ''}`}
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,application/pdf"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            {vehicleFile ? (
              <>
                <svg className="cc-upload-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <span className="cc-upload-text">{vehicleFile.name}</span>
                <span className="cc-upload-sub">Click to change file</span>
              </>
            ) : (
              <>
                <svg className="cc-upload-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
                <span className="cc-upload-text">Upload photo of your vehicle</span>
                <span className="cc-upload-sub">PNG, JPG or PDF up to 10MB</span>
              </>
            )}
          </div>
        </div>

        {/* Error */}
        {error && <p className="cc-error">{error}</p>}

      </div>

      {/* ── Submit ── */}
      <button
        className="cc-submit-btn"
        onClick={handleSubmit}
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Submitting…' : 'Submit Application →'}
      </button>
    </div>
  );
}

export default CurierComfirm;